// interactions/electrostatics.js
/**
 * @fileoverview Coulomb E-field, potential, and force computations for
 * pocket electrostatics visualization.
 *
 * Uses partial charges already stored on each atom (atom.charge) and
 * a simple distance-based Coulomb model with a uniform dielectric.
 *
 * All energies in kcal/mol, distances in Angstroms, charges in
 * elementary-charge units (e).
 */
import {
    Length3, Mul3_1, Dot3, Cross3,
} from 'BMapsSrc/math';

// Coulomb constant in kcal*A*mol^-1*e^-2
const K_COULOMB = 332.0637;
const EPSILON = 4.0;
const K_EFF = K_COULOMB / EPSILON; // ~83.016

// Minimum squared distance to avoid singularities
const R_MIN_SQ = 0.25;

/**
 * Build a flat typed-array snapshot of every charge source in the supplied
 * AtomGroups, for use by the Coulomb sums in computeElectricField /
 * computePotential. Accepts any AtomGroups (protein chains, cofactors, ions,
 * residues) — no protein/non-protein distinction.
 *
 * Filters per-atom: missing position, or zero/missing charge (Number(charge)
 * || 0 → 0 → dropped). NO distance cutoff: partial-residue inclusion would
 * create artificial net charges, which is unphysical. If we ever need a
 * cutoff, it must be applied per-residue (all-or-nothing), not per-atom.
 *
 * @param {AtomGroup[]} atomGroups
 * @returns {{ positions: Float64Array, charges: Float64Array, count: number }}
 */
export function buildProteinSubset(atomGroups) {
    const allAtoms = [];
    for (const grp of atomGroups) {
        for (const atom of grp.getAtoms()) {
            const pos = atom.getPosition();
            if (!pos) continue;
            const chg = Number(atom.charge) || 0;
            if (chg !== 0) {
                allAtoms.push(pos[0], pos[1], pos[2], chg);
            }
        }
    }

    const count = allAtoms.length / 4;
    const positions = new Float64Array(count * 3);
    const charges = new Float64Array(count);
    for (let i = 0; i < count; i++) {
        positions[i * 3] = allAtoms[i * 4];
        positions[i * 3 + 1] = allAtoms[i * 4 + 1];
        positions[i * 3 + 2] = allAtoms[i * 4 + 2];
        charges[i] = allAtoms[i * 4 + 3];
    }
    return { positions, charges, count };
}

/**
 * Compute E-field at a single point from a protein subset.
 * E(r) = k_eff * sum_j Q_j * (r - R_j) / |r - R_j|^3
 *
 * @param {{ positions: Float64Array, charges: Float64Array, count: number }} subset
 * @param {number[]} point  [x, y, z]
 * @returns {number[]} [Ex, Ey, Ez]
 */
export function computeElectricField(subset, point) {
    const { positions, charges, count } = subset;
    let ex = 0;
    let ey = 0;
    let ez = 0;
    const px = point[0];
    const py = point[1];
    const pz = point[2];

    for (let j = 0; j < count; j++) {
        const j3 = j * 3;
        const dx = px - positions[j3];
        const dy = py - positions[j3 + 1];
        const dz = pz - positions[j3 + 2];
        const r2 = dx * dx + dy * dy + dz * dz;
        if (r2 < R_MIN_SQ) continue;
        const r = Math.sqrt(r2);
        const r3 = r2 * r;
        const factor = (K_EFF * charges[j]) / r3;
        ex += factor * dx;
        ey += factor * dy;
        ez += factor * dz;
    }
    return [ex, ey, ez];
}

/**
 * Compute electrostatic potential at a single point.
 * V(r) = k_eff * sum_j Q_j / |r - R_j|
 *
 * @param {{ positions: Float64Array, charges: Float64Array, count: number }} subset
 * @param {number[]} point  [x, y, z]
 * @returns {number}
 */
export function computePotential(subset, point) {
    const { positions, charges, count } = subset;
    let v = 0;
    const px = point[0];
    const py = point[1];
    const pz = point[2];

    for (let j = 0; j < count; j++) {
        const j3 = j * 3;
        const dx = px - positions[j3];
        const dy = py - positions[j3 + 1];
        const dz = pz - positions[j3 + 2];
        const r2 = dx * dx + dy * dy + dz * dz;
        if (r2 < R_MIN_SQ) continue;
        v += (K_EFF * charges[j]) / Math.sqrt(r2);
    }
    return v;
}

/**
 * Compute E-field and force at each ligand atom.
 * Force on atom i: F_i = Q_i * E(r_i)
 *
 * @param {{ positions: Float64Array, charges: Float64Array, count: number }} subset
 * @param {Atom[]} ligandAtoms
 * @returns {Array<{ atom: Atom, field: number[], magnitude: number,
 *                   force: number[], forceMagnitude: number }>}
 *   Sorted by forceMagnitude descending.
 */
export function computeFieldAtAtoms(subset, ligandAtoms) {
    const results = [];
    for (const atom of ligandAtoms) {
        const pos = atom.getPosition();
        if (!pos) continue;
        const chg = Number(atom.charge) || 0;
        const field = computeElectricField(subset, pos);
        const magnitude = Length3(field);
        const force = Mul3_1(field, chg);
        const forceMagnitude = Math.abs(chg) * magnitude;
        results.push({
            atom, field, magnitude, force, forceMagnitude,
        });
    }
    results.sort((a, b) => b.forceMagnitude - a.forceMagnitude);
    return results;
}

/**
 * Dipole-field interaction energy and torque.
 * energy = -p . E(COM), torque = p x E(COM)
 *
 * @param {{ positions: Float64Array, charges: Float64Array, count: number }} subset
 * @param {{ dipole: number[], centerOfMass: number[] }} dipoleInfo
 * @returns {{ energy: number, torque: number[] }}
 */
export function computeDipoleFieldInteraction(subset, dipoleInfo) {
    const field = computeElectricField(subset, dipoleInfo.centerOfMass);
    const energy = -Dot3(dipoleInfo.dipole, field);
    const torque = Cross3(dipoleInfo.dipole, field);
    return { energy, torque };
}

/**
 * Charge-potential interaction.
 * Per-atom: energy_i = Q_i * V(r_i)
 *
 * @param {{ positions: Float64Array, charges: Float64Array, count: number }} subset
 * @param {Atom[]} ligandAtoms
 * @returns {{ totalEnergy: number, perAtom: Array<{ atom: Atom, energy: number }> }}
 */
export function computeChargeInteraction(subset, ligandAtoms) {
    let totalEnergy = 0;
    const perAtom = [];
    for (const atom of ligandAtoms) {
        const pos = atom.getPosition();
        if (!pos) continue;
        const chg = Number(atom.charge) || 0;
        const v = computePotential(subset, pos);
        const energy = chg * v;
        totalEnergy += energy;
        perAtom.push({ atom, energy });
    }
    return { totalEnergy, perAtom };
}
